import PropTypes from 'prop-types';
import React, { useEffect, useMemo, memo } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { bindActionCreators } from 'redux';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import * as actions from '../actions/windward';
import { Windward as WindwardPage } from '../components/Windward';
import { showError } from '../utils/MessagePopup';
import { usePopupContext } from '../contexts/PopupContext';

const Windward = ({
  deleteWindwardTemplate,
  fetchWindwardReports,
  fetchWindwardTemplates,
  isDeleting,
  isLoading,
  uploadTemplate,
  templates,
  reports,
}) => {
  const { openPopup, closePopup } = usePopupContext();
  const transformedTemplates = useMemo(() => templates.toJS(), [templates]);
  const transformedReports = useMemo(() => reports.toJS(), [reports]);

  useEffect(() => {
    fetchWindwardTemplates();
    fetchWindwardReports();
  }, [fetchWindwardTemplates, fetchWindwardReports]);

  const handleUpload = (event, module, fileName) => {
    event.preventDefault();

    const { currentTarget } = event;
    const formData = new FormData(currentTarget);

    formData.delete('module');
    formData.set('module_id', module);
    formData.set('file_name', fileName);

    const afterSuccess = () => {
      fetchWindwardTemplates();
      closePopup();
    };

    const afterError = ({ response }) => {
      const errors = response.body.errors.map(error => error.messages.join('\n'));

      showError(openPopup, errors);
    };

    uploadTemplate({ formData, module, afterSuccess, afterError });
  };

  const handleDelete = rowInfo => {
    const afterSuccess = () => fetchWindwardTemplates();

    deleteWindwardTemplate({
      moduleId: rowInfo.moduleId,
      fileName: rowInfo.title,
      afterSuccess,
    });
  };

  const handleOpenTemplateUploader = () => {
    openPopup('UploadTemplatePopup', {
      onUpload: (event, module, fileName) => handleUpload(event, module, fileName),
    });
  };

  return (
    <>
      <Helmet title="Windward Templates" />
      <WindwardPage
        isLoading={isLoading || isDeleting}
        onDelete={handleDelete}
        onRefreshReportsClick={fetchWindwardReports}
        onRefreshTemplatesClick={fetchWindwardTemplates}
        onUploadClick={handleOpenTemplateUploader}
        reports={transformedReports}
        templates={transformedTemplates}
      />
    </>
  );
};

Windward.propTypes = {
  deleteWindwardTemplate: PropTypes.func.isRequired,
  fetchWindwardReports: PropTypes.func.isRequired,
  fetchWindwardTemplates: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  isLoading: PropTypes.bool,
  reports: PropTypes.object.isRequired,
  templates: PropTypes.object.isRequired,
  uploadTemplate: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  templates: state.windward.get('templates'),
  reports: state.windward.get('reports'),
  inputErrors: state.windward.get('inputErrors'),
  isLoading: state.windward.get('loadingReports') || state.windward.get('loadingTemplates'),
  isDeleting: state.windward.get('isDeleting'),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(actions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(Windward));
